import React, { useState, useRef, useEffect } from 'react';
import { 
  UploadCloud, 
  Bot, 
  CheckCircle2, 
  AlertCircle, 
  X, 
  File as FileIcon, 
  Image as ImageIcon,
  FileText,
  FileSpreadsheet,
  Presentation,
  Trash2,
  Tag,
  Euro,
  MapPin,
  RefreshCw,
  FolderOpen,
  Download
} from 'lucide-react';
import { scanDocumentIA, exportAIData } from '../../../api/aiAPI';
import { uploadDocument } from '../../forum/api/forumAPI';
import { uploadDocument as uploadGenericDocument } from '../api/documentsAPI';
import { useNotification } from '../../../context/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';

const MAX_SIZE_SIMPLE_MB = 5;
const MAX_SIZE_IA_MB = 10;
const CATEGORIES = ['Contrat', 'Analyse', 'Justificatif', 'Identité', 'Autre'];

const getFileIcon = (type, name) => {
  if (type.startsWith('image/')) return <ImageIcon className="w-8 h-8 text-info-500" />;
  if (type === 'application/pdf') return <FileText className="w-8 h-8 text-danger-500" />;
  if (name.endsWith('.doc') || name.endsWith('.docx')) return <FileText className="w-8 h-8 text-info-600" />;
  if (name.endsWith('.xls') || name.endsWith('.xlsx')) return <FileSpreadsheet className="w-8 h-8 text-success-600" />;
  if (name.endsWith('.ppt') || name.endsWith('.pptx')) return <Presentation className="w-8 h-8 text-orange-500" />;
  return <FileIcon className="w-8 h-8 text-ink-500" />;
};

const getSizeColor = (sizeMb) => {
  if (sizeMb < 2) return 'bg-success-100 text-success-800';
  if (sizeMb < 8) return 'bg-amber-100 text-amber-800';
  return 'bg-danger-100 text-danger-800';
};

export default function DocumentUploader({ dossierId, transactionId, onUploadComplete, onScanComplete }) {
  const { showToast } = useNotification();
  const [activeTab, setActiveTab] = useState('simple'); // 'simple' | 'ia'
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]); // Array for simple, single item array for IA
  const [category, setCategory] = useState('');
  
  // Upload State
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(''); // 'Envoi...', 'Analyse IA...', etc.
  
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Reset state when tab changes
  useEffect(() => {
    setFiles([]);
    setScanResult(null);
    setError(null);
    setProgress(0);
    setCategory('');
  }, [activeTab]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave" || e.type === "drop") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (selectedFiles) => {
    setError(null);
    setScanResult(null);

    let newFiles = [...selectedFiles];

    if (activeTab === 'ia') {
      // IA mode only accepts one file and specific formats
      newFiles = [newFiles[0]];
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ];

      if (!allowedTypes.includes(newFiles[0].type)) {
        setError("Format non supporté pour le scan IA. Veuillez utiliser PDF, Word, Excel ou PPT.");
        return;
      }
      
      const sizeMb = newFiles[0].size / 1024 / 1024;
      if (sizeMb > MAX_SIZE_IA_MB) {
        setError(`Fichier trop volumineux. Maximum ${MAX_SIZE_IA_MB}MB pour le scan IA.`);
        return;
      }
      
      setFiles(newFiles);
    } else {
      // Simple mode: multiple files, filter out too large files
      const validFiles = [];
      let hasTooLarge = false;
      
      const combinedFiles = [...files, ...newFiles];
      
      combinedFiles.forEach(file => {
        const sizeMb = file.size / 1024 / 1024;
        if (sizeMb <= MAX_SIZE_SIMPLE_MB) {
          // ensure no duplicates by name (simple check)
          if (!validFiles.find(f => f.name === file.name)) {
             validFiles.push(file);
          }
        } else {
          hasTooLarge = true;
        }
      });

      if (hasTooLarge) {
        showToast(`Certains fichiers dépassent la limite de ${MAX_SIZE_SIMPLE_MB}MB et ont été ignorés.`, 'warning');
      }
      setFiles(validFiles);
    }
  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  const clearAll = () => {
    setFiles([]);
    setScanResult(null);
    setError(null);
    setCategory('');
    setProgress(0);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    setError(null);
    setProgress(0);

    try {
      if (activeTab === 'ia') {
        setUploadStatus('Envoi au serveur IA (0%)...');
        
        try {
          const result = await scanDocumentIA(files[0], (percent) => {
            if (percent < 100) {
              setProgress(percent / 2); // First 50% is upload
              setUploadStatus(`Envoi au serveur IA (${percent}%)...`);
            } else {
              setProgress(50);
              setUploadStatus('Analyse IA en cours (Llama 3.2)...');
              
              // Start a slow faux progress for the remaining 50% while waiting for the LLM
              // since we can't track LLM progress easily.
              const interval = setInterval(() => {
                setProgress(prev => {
                  if (prev >= 95) {
                    clearInterval(interval);
                    return 95;
                  }
                  return prev + 5;
                });
              }, 2000);
              
              // Store interval to clear it later if needed (though we'll just let it hit 95)
              window._aiProgressInterval = interval;
            }
          });
          
          if (window._aiProgressInterval) clearInterval(window._aiProgressInterval);

          if (result.success) {
             setProgress(100);
             setScanResult(result.data);
             if (result.data.operationType && !category) {
                 setCategory(result.data.operationType);
             }
             showToast("Analyse terminée avec succès", "success");
             if (onScanComplete) onScanComplete(result.data);
          } else {
             setError(result.error || "L'analyse IA a échoué.");
          }
        } catch(e) {
           setError("Erreur de connexion au serveur d'analyse.");
        } finally {
           if (window._aiProgressInterval) clearInterval(window._aiProgressInterval);
           setIsUploading(false);
        }
      } else {
        setUploadStatus('Envoi en cours...');
        
        let allSuccess = true;
        
        // When uploading multiple files, we distribute the 100% across all files
        const progressPerFile = 100 / files.length;
        
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const baseProgress = i * progressPerFile;
            
            try {
                if (transactionId) {
                  const formData = new FormData();
                  formData.append('file', file);
                  formData.append('transactionId', transactionId);
                  if (category) formData.append('category', category);

                  await uploadGenericDocument(formData, (percentCompleted) => {
                     const fileContribution = (percentCompleted / 100) * progressPerFile;
                     setProgress(baseProgress + fileContribution);
                  });
                } else {
                  await uploadDocument(dossierId, file, category, (percentCompleted) => {
                     const fileContribution = (percentCompleted / 100) * progressPerFile;
                     setProgress(baseProgress + fileContribution);
                  });
                }
            } catch (uploadError) {
                console.error("Erreur d'upload", uploadError);
                allSuccess = false;
                if (uploadError.response?.status === 413) {
                    showToast(`Le fichier ${file.name} est trop volumineux.`, "error");
                } else {
                    showToast(`Échec de l'upload pour ${file.name}`, "error");
                }
            }
        }

        setIsUploading(false);
        if (allSuccess) {
           setFiles([]);
           setProgress(100);
           showToast(`${files.length} document(s) uploadé(s) avec succès.`, "success");
           if (onUploadComplete) onUploadComplete(files, category);
        } else {
           setError("Certains fichiers n'ont pas pu être envoyés.");
        }
      }
    } catch (err) {
      console.error(err);
      setError("Une erreur s'est produite lors de l'envoi.");
      setIsUploading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="workspace-card-elevated overflow-hidden bg-surface rounded-xl border border-workspace shadow-hp-md">
      {/* Tabs */}
      <div className="flex border-b border-workspace relative bg-surface-muted">
        <button
          onClick={() => setActiveTab('simple')}
          className={`flex-1 py-4 flex justify-center items-center font-semibold text-sm transition-colors relative z-10 ${
            activeTab === 'simple' ? 'text-primary' : 'text-tertiary hover:text-primary'
          }`}
        >
          <UploadCloud className={`w-5 h-5 mr-2 transition-transform ${activeTab === 'simple' ? 'scale-110' : ''}`} />
          Import Simple
        </button>
        <button
          onClick={() => setActiveTab('ia')}
          className={`flex-1 py-4 flex justify-center items-center font-semibold text-sm transition-colors relative z-10 ${
            activeTab === 'ia' ? 'text-secondary' : 'text-tertiary hover:text-primary'
          }`}
        >
          <Bot className={`w-5 h-5 mr-2 transition-transform ${activeTab === 'ia' ? 'scale-110' : ''}`} />
          Scan IA Agent
        </button>
        
        {/* Sliding Indicator */}
        <div className="absolute bottom-0 left-0 w-full h-[2px] flex">
          <motion.div 
            className="h-full bg-secondary"
            initial={false}
            animate={{ 
               width: '50%',
               x: activeTab === 'simple' ? '0%' : '100%'
            }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          />
        </div>
      </div>

      <div className="p-6">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
           <div>
             <h4 className="text-sm font-bold text-primary mb-1">
               {activeTab === 'simple' ? 'Dépôt de documents' : 'Extraction intelligente'}
             </h4>
             <p className="text-xs text-tertiary">
               {activeTab === 'simple'
                 ? 'Sélectionnez un ou plusieurs documents (tous formats acceptés).'
                 : 'Uploadez un fichier unique (PDF, Word, Excel) pour extraire automatiquement les informations clés.'}
             </p>
           </div>
        </div>

        {/* Dropzone */}
        {files.length === 0 ? (
          <motion.div
            whileHover={{ scale: 1.01 }}
            className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer relative overflow-hidden ${
              dragActive 
                ? 'border-secondary bg-[var(--hp-bronze-100)]/30' 
                : 'border-workspace hover:border-secondary hover:bg-surface-muted'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            animate={dragActive ? { 
              borderColor: ['var(--hp-color-border)', 'var(--hp-bronze-500)', 'var(--hp-color-border)'] 
            } : {}}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={handleChange}
              multiple={activeTab === 'simple'}
              accept={activeTab === 'ia' ? ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" : undefined}
            />

            <motion.div
               animate={dragActive ? { y: [0, -10, 0] } : {}}
               transition={{ repeat: Infinity, duration: 1 }}
            >
              {activeTab === 'simple' ? (
                <FolderOpen className="w-12 h-12 mx-auto text-tertiary mb-4" />
              ) : (
                <Bot className="w-12 h-12 mx-auto text-secondary mb-4" />
              )}
            </motion.div>

            <p className="text-sm text-primary font-medium mb-1">
              Cliquez pour sélectionner ou glissez vos fichiers ici
            </p>
            <p className="text-xs text-tertiary">
              {activeTab === 'ia' 
                ? `PDF, Word, Excel (Max ${MAX_SIZE_IA_MB}MB)` 
                : `Tout type de fichier (Max ${MAX_SIZE_SIMPLE_MB}MB)`}
            </p>
          </motion.div>
        ) : (
          /* File List */
          <div className="space-y-3">
             <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-primary">Fichiers sélectionnés ({files.length})</span>
                {!isUploading && !scanResult && (
                  <button onClick={clearAll} className="text-xs text-danger-500 hover:text-danger-700 font-medium flex items-center">
                    <Trash2 className="w-3 h-3 mr-1" /> Tout supprimer
                  </button>
                )}
             </div>
             
             <div className="max-h-60 overflow-y-auto space-y-2 pr-2 scrollbar-thin scrollbar-thumb-gray-300">
               <AnimatePresence>
                 {files.map((file, index) => {
                   const sizeMb = file.size / 1024 / 1024;
                   return (
                     <motion.div 
                        key={`${file.name}-${index}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="border border-workspace rounded-lg p-3 bg-surface flex items-center justify-between group"
                     >
                        <div className="flex items-center overflow-hidden flex-1">
                           {getFileIcon(file.type, file.name)}
                           <div className="ml-3 truncate">
                              <p className="text-sm font-medium text-primary truncate">{file.name}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${getSizeColor(sizeMb)}`}>
                                  {sizeMb.toFixed(2)} MB
                                </span>
                                {file.name.split('.').pop() && (
                                   <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-ink-100 text-ink-600 uppercase">
                                     {file.name.split('.').pop()}
                                   </span>
                                )}
                              </div>
                           </div>
                        </div>
                        {!isUploading && !scanResult && (
                          <button
                            onClick={() => removeFile(index)}
                            className="p-2 text-tertiary hover:text-danger-500 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                            aria-label="Supprimer le fichier"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                     </motion.div>
                   )
                 })}
               </AnimatePresence>
             </div>
          </div>
        )}

        {/* Error State */}
        <AnimatePresence>
          {error && (
            <motion.div 
               initial={{ opacity: 0, height: 0 }}
               animate={{ opacity: 1, height: 'auto' }}
               exit={{ opacity: 0, height: 0 }}
               className="mt-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700 flex items-start overflow-hidden"
            >
              <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Upload Progress */}
        <AnimatePresence>
           {isUploading && (
             <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6"
             >
                <div className="flex justify-between items-center mb-2">
                   <span className="text-sm font-medium text-primary flex items-center">
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin text-secondary" />
                      {uploadStatus}
                   </span>
                   <span className="text-sm font-bold text-secondary">{Math.round(progress)}%</span>
                </div>
                <div className="h-2 w-full bg-surface-muted rounded-full overflow-hidden">
                   <motion.div 
                      className="h-full bg-secondary"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ ease: "linear" }}
                   />
                </div>
             </motion.div>
           )}
        </AnimatePresence>

        {/* Scan Results (IA mode) */}
        <AnimatePresence>
          {scanResult && (
            <motion.div 
               variants={containerVariants}
               initial="hidden"
               animate="visible"
               exit={{ opacity: 0, y: 20 }}
               className="mt-6"
            >
               <div className="p-5 bg-success-50 border border-success-200 rounded-xl">
                 <div className="flex items-center mb-4 pb-4 border-b border-success-200/60">
                   <motion.div 
                      initial={{ scale: 0 }} 
                      animate={{ scale: 1 }} 
                      transition={{ type: "spring", bounce: 0.5 }}
                   >
                     <CheckCircle2 className="w-6 h-6 text-success-600 mr-2" />
                   </motion.div>
                   <h5 className="font-bold text-success-800">Scan IA réussi</h5>
                 </div>
                 
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    {scanResult.title && (
                      <motion.div variants={itemVariants} className="bg-white/60 p-3 rounded-lg border border-success-100 flex items-start">
                         <FileText className="w-4 h-4 text-success-600 mt-0.5 mr-2 shrink-0" />
                         <div>
                            <p className="text-[10px] uppercase font-bold text-success-600/70 mb-0.5">Titre suggéré</p>
                            <p className="text-sm font-medium text-success-900">{scanResult.title}</p>
                         </div>
                      </motion.div>
                    )}
                    {scanResult.operationType && (
                      <motion.div variants={itemVariants} className="bg-white/60 p-3 rounded-lg border border-success-100 flex items-start">
                         <Tag className="w-4 h-4 text-success-600 mt-0.5 mr-2 shrink-0" />
                         <div>
                            <p className="text-[10px] uppercase font-bold text-success-600/70 mb-0.5">Type de document</p>
                            <p className="text-sm font-medium text-success-900">{scanResult.operationType}</p>
                         </div>
                      </motion.div>
                    )}
                    {scanResult.requestedAmount && (
                      <motion.div variants={itemVariants} className="bg-white/60 p-3 rounded-lg border border-success-100 flex items-start">
                         <Euro className="w-4 h-4 text-success-600 mt-0.5 mr-2 shrink-0" />
                         <div>
                            <p className="text-[10px] uppercase font-bold text-success-600/70 mb-0.5">Montant détecté</p>
                            <p className="text-sm font-medium text-success-900">{scanResult.requestedAmount}</p>
                         </div>
                      </motion.div>
                    )}
                    {scanResult.location && (
                      <motion.div variants={itemVariants} className="bg-white/60 p-3 rounded-lg border border-success-100 flex items-start">
                         <MapPin className="w-4 h-4 text-success-600 mt-0.5 mr-2 shrink-0" />
                         <div>
                            <p className="text-[10px] uppercase font-bold text-success-600/70 mb-0.5">Localisation</p>
                            <p className="text-sm font-medium text-success-900">{scanResult.location}</p>
                         </div>
                      </motion.div>
                    )}
                 </div>

                 {scanResult.summary && (
                   <motion.div variants={itemVariants} className="bg-white/80 p-4 rounded-lg border-l-2 border-success-500 text-sm text-success-900 italic shadow-sm mb-4">
                     "{scanResult.summary}"
                   </motion.div>
                 )}

                 <motion.div variants={itemVariants} className="flex flex-wrap gap-2 mb-4">
                    <p className="text-xs font-bold text-success-700 w-full mb-1">Exporter les données :</p>
                    <button 
                       onClick={() => exportAIData('excel', scanResult)}
                       className="btn btn-secondary !px-2 !py-1 text-xs flex items-center bg-white"
                    >
                       <FileSpreadsheet className="w-3 h-3 mr-1 text-success-600" /> Excel
                    </button>
                    <button 
                       onClick={() => exportAIData('word', scanResult)}
                       className="btn btn-secondary !px-2 !py-1 text-xs flex items-center bg-white"
                    >
                       <FileText className="w-3 h-3 mr-1 text-info-600" /> Word
                    </button>
                    <button 
                       onClick={() => exportAIData('pdf', scanResult)}
                       className="btn btn-secondary !px-2 !py-1 text-xs flex items-center bg-white"
                    >
                       <FileText className="w-3 h-3 mr-1 text-danger-600" /> PDF
                    </button>
                 </motion.div>

                 <motion.div variants={itemVariants} className="flex flex-wrap gap-3 mt-2">
                    <button 
                       onClick={() => {
                          showToast("Données extraites appliquées au dossier", "success");
                          clearAll();
                       }}
                       className="btn btn-primary bg-success-600 border-success-600 hover:bg-success-700 hover:border-success-700 flex-1 sm:flex-none"
                    >
                       Appliquer au dossier
                    </button>
                    <button 
                       onClick={clearAll}
                       className="btn btn-secondary text-success-700 border-success-300 hover:bg-success-100 flex-1 sm:flex-none"
                    >
                       Scanner un autre document
                    </button>
                 </motion.div>
               </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action Button */}
        {files.length > 0 && !scanResult && !isUploading && (
          <motion.div 
             initial={{ opacity: 0, y: 10 }}
             animate={{ opacity: 1, y: 0 }}
             className="mt-6 flex justify-end"
          >
            <button
              onClick={handleUpload}
              className={`btn ${activeTab === 'ia' ? 'btn-accent' : 'btn-primary'} w-full sm:w-auto shadow-hp-sm hover:shadow-hp-md`}
            >
              {activeTab === 'ia' ? (
                 <><Bot className="w-4 h-4 mr-2" /> Lancer l'analyse IA</>
              ) : (
                 <><UploadCloud className="w-4 h-4 mr-2" /> Uploader {files.length} fichier(s)</>
              )}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
